/**
 * TerrainToolCard — Card 3 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.3.
 *
 * Hillshade toggle, elevation profile (`ElevationProfilePanel`), contours
 * with interval slider, slope / aspect toggles, vertical exaggeration
 * slider with mono `×` readout, terrain opacity, drape mode radio chip
 * group, cut/fill placeholder chip, and coordinate / elevation query
 * mono readout row.
 *
 * Mutations route through `useViewerStore.setTerrainToolCard` so adapter
 * mocks / tests observe a stable `TerrainToolCardState` shape.
 */

import { useMemo } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  Banner,
  Callout,
  Chip,
  Icon,
  tokens,
} from '@/design';
import {
  ToolCardSection,
  ToolCardShell,
  ToolCardSliderRow,
  ToolCardToggleRow,
} from '@/components/toolcards/ToolCardShell';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import { TERRAIN_ELEVATION_MESH } from '@/lib/featureFlags';
import {
  ElevationProfilePanel,
  type ElevationProfileSample,
} from './ElevationProfilePanel';
import { TerrainColorMapPicker } from './TerrainColorMapPicker';
import {
  hasAnyTerrainDerivative,
  resolveTerrainDerivatives,
} from './terrainDerivedLayers';

export interface TerrainToolCardProps {
  /** The active terrain / DEM layer; carries metadata facts. */
  layer?: Layer | null;
  /**
   * G20 — full catalog so the card can resolve RFC-108-G alternates /
   * companion layers (hillshade / slope / aspect / contours COGs + PMTiles)
   * and disable controls whose data prerequisite is absent (no dead buttons).
   * Optional for back-compat with existing tests that only pass `layer`.
   */
  layers?: readonly Layer[];
  /** G20 — toggle a companion layer's visibility (drives mount in TwinShell). */
  onSetLayerVisible?: (layerId: string, visible: boolean) => void;
  /**
   * G20 — current visibility map (id → visible). Used so toggles in this card
   * reflect the live store state rather than the tool-card-local state.
   */
  visibleLayerIds?: ReadonlySet<string>;
  /** Picked elevation samples to forward to the profile panel. */
  profileSamples?: readonly ElevationProfileSample[];
  /** Callback when the user starts picking a profile on the map. */
  onPickProfile?: () => void;
  /** Callback to open the full profile workspace drawer. */
  onOpenFullProfile?: () => void;
  /** Cut / fill flow callback (placeholder coming in WP-7+). */
  onCutFill?: () => void;
  /** Read-only / permission flag from the gTwin permission model. */
  readOnly?: boolean;
  permissionDenied?: string | null;
  /** Force render outside the tool-card kind gate (for tests / preview). */
  forceVisible?: boolean;
  'data-testid'?: string;
}

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatVerticalExaggeration(v: number): string {
  // Card 3 requires mono numeric readout; one decimal is enough for 1×–20×.
  return `${v.toFixed(1)}×`;
}

function formatMetres(v: number): string {
  return `${v.toFixed(0)} m`;
}

export function TerrainToolCard({
  layer,
  layers,
  onSetLayerVisible,
  visibleLayerIds,
  profileSamples,
  onPickProfile,
  onOpenFullProfile,
  onCutFill,
  readOnly,
  permissionDenied,
  forceVisible,
  'data-testid': testId,
}: TerrainToolCardProps) {
  const toolCardKind = useViewerSlice(
    (s) => s.sceneSelection.toolCardKind ?? 'gtwin',
  );
  const state = useViewerSlice((s) => s.terrainToolCard);
  const setTerrainToolCard = useViewerStore((s) => s.setTerrainToolCard);

  // G20 — resolve derived terrain companions in the catalog. Memoized on
  // identity so this stays cheap when the catalog is large.
  const derivatives = useMemo(
    () => resolveTerrainDerivatives(layer ?? null, layers ?? []),
    [layer, layers],
  );
  const anyDerivative = hasAnyTerrainDerivative(derivatives);
  const liveVisible = visibleLayerIds ?? null;
  const isCompanionVisible = (companion: Layer | null): boolean => {
    if (!companion) return false;
    if (liveVisible) return liveVisible.has(companion.id);
    // No live store — fall back to the tool-card-local state.
    return false;
  };
  const toggleCompanion = (companion: Layer | null, on: boolean): void => {
    if (!companion || !onSetLayerVisible) return;
    onSetLayerVisible(companion.id, on);
  };

  const visible = forceVisible || toolCardKind === 'terrain';
  if (!visible) return null;

  return (
    <ToolCardShell
      icon="terrain"
      title="Terrain & DEM"
      subtitle={layer?.label ?? layer?.id ?? null}
      data-testid={testId ?? 'terrain-toolcard'}
      topBanners={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
          }}
        >
          {permissionDenied ? (
            <Banner
              severity="warning"
              title="Terrain restricted"
              body={permissionDenied}
              narrationKey="terrain-toolcard-permission"
            />
          ) : null}
          {readOnly ? (
            <Chip icon="visibility" variant="tertiary" label="Read-only" />
          ) : null}
          {!layer ? (
            <Chip
              icon="block"
              variant="neutral"
              label="No terrain layer selected"
            />
          ) : null}
          {layer && !anyDerivative ? (
            <Callout
              severity="info"
              icon="info"
              data-testid="terrain-no-derivatives-callout"
            >
              No derived terrain data is wired to this DEM. Hillshade, slope,
              aspect, and contour controls stay disabled until the converter
              emits derivative COGs / PMTiles (<code>terrain-derived-*</code>{' '}
              / <code>dem-contours</code>) and the operator links them via{' '}
              <code>derivedFrom</code> or{' '}
              <code>style.terrainDerivative</code>.
            </Callout>
          ) : null}
        </div>
      }
    >
      <ToolCardSection title="Display">
        <ToolCardSliderRow
          label="Terrain opacity"
          icon="tune"
          min={0}
          max={1}
          step={0.05}
          value={state.opacity}
          readout={formatPercent(state.opacity)}
          onChange={(next) => setTerrainToolCard({ opacity: next })}
          data-testid="terrain-opacity-slider"
        />
        <ToolCardSliderRow
          label="Vertical exaggeration"
          icon="swap_vert"
          min={1}
          // Max raised 20 → 100 (gap G20): shallow DEMs (e.g. ~6.7 m relief)
          // are only inspectable at large exaggeration. Engine wiring is
          // `scene.scale.z` via setVerticalExaggeration.
          max={100}
          step={0.5}
          value={state.verticalExaggeration}
          readout={formatVerticalExaggeration(state.verticalExaggeration)}
          // Vertical exaggeration requires a Giro3D ElevationLayer (true 3D
          // elevation-mesh). The elevation-mesh terrain path is gated behind the
          // VITE_FEATURE_TERRAIN_ELEVATION_MESH spike flag (gap G20). When the
          // flag is OFF the terrain renders as a flat ColorLayer, so scaling the
          // (zero-height) mesh is a no-op and the control stays disabled.
          disabled={!TERRAIN_ELEVATION_MESH}
          onChange={(next) =>
            setTerrainToolCard({ verticalExaggeration: next })
          }
          data-testid="terrain-vex-slider"
        />
        {!TERRAIN_ELEVATION_MESH && (
          <Callout
            severity="info"
            icon="info"
            data-testid="terrain-vex-parked-callout"
          >
            Vertical exaggeration requires the elevation-mesh terrain path
            (Giro3D ElevationLayer), which is currently parked pending
            stability work. Emitting derivative COGs will not enable this
            control.
          </Callout>
        )}
      </ToolCardSection>

      <ToolCardSection title="Color ramp">
        <TerrainColorMapPicker
          activeRamp={state.colorRamp}
          onChange={(ramp) => setTerrainToolCard({ colorRamp: ramp })}
          data-testid="terrain-color-ramp"
        />
      </ToolCardSection>

      <ToolCardSection title="Derived surfaces">
        <ToolCardToggleRow
          label="Hillshade"
          icon="wb_sunny"
          description={
            derivatives.hillshade
              ? 'Sun-illuminated shading from the DEM.'
              : 'No hillshade derivative available for this DEM.'
          }
          checked={
            derivatives.hillshade
              ? isCompanionVisible(derivatives.hillshade) || state.hillshade
              : state.hillshade
          }
          disabled={!derivatives.hillshade}
          onChange={(next) => {
            setTerrainToolCard({ hillshade: next });
            toggleCompanion(derivatives.hillshade, next);
          }}
          data-testid="terrain-hillshade-toggle"
        />
        <ToolCardToggleRow
          label="Slope"
          icon="landscape"
          description={
            derivatives.slope
              ? undefined
              : 'No slope derivative available for this DEM.'
          }
          checked={
            derivatives.slope
              ? isCompanionVisible(derivatives.slope) || state.slopeOn
              : state.slopeOn
          }
          disabled={!derivatives.slope}
          onChange={(next) => {
            setTerrainToolCard({ slopeOn: next });
            toggleCompanion(derivatives.slope, next);
          }}
          data-testid="terrain-slope-toggle"
        />
        <ToolCardToggleRow
          label="Aspect"
          icon="north"
          description={
            derivatives.aspect
              ? undefined
              : 'No aspect derivative available for this DEM.'
          }
          checked={
            derivatives.aspect
              ? isCompanionVisible(derivatives.aspect) || state.aspectOn
              : state.aspectOn
          }
          disabled={!derivatives.aspect}
          onChange={(next) => {
            setTerrainToolCard({ aspectOn: next });
            toggleCompanion(derivatives.aspect, next);
          }}
          data-testid="terrain-aspect-toggle"
        />
      </ToolCardSection>

      <ToolCardSection title="Contours">
        <ToolCardToggleRow
          label="Show contours"
          icon="landscape"
          description={
            derivatives.contours
              ? undefined
              : 'No contour PMTiles available for this DEM.'
          }
          checked={
            derivatives.contours
              ? isCompanionVisible(derivatives.contours) || state.contoursOn
              : state.contoursOn
          }
          disabled={!derivatives.contours}
          onChange={(next) => {
            setTerrainToolCard({ contoursOn: next });
            toggleCompanion(derivatives.contours, next);
          }}
          data-testid="terrain-contours-toggle"
        />
        {state.contoursOn && derivatives.contours ? (
          <ToolCardSliderRow
            label="Interval"
            icon="straighten"
            min={1}
            max={100}
            step={1}
            value={state.contourInterval}
            readout={formatMetres(state.contourInterval)}
            onChange={(next) =>
              setTerrainToolCard({ contourInterval: next })
            }
            data-testid="terrain-contour-interval"
          />
        ) : null}
      </ToolCardSection>

      <ToolCardSection
        title="Drape"
        description="Drape raster or vector layers across the terrain mesh."
      >
        <div
          role="radiogroup"
          aria-label="Drape mode"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[1],
          }}
        >
          {(['none', 'raster', 'vector'] as const).map((mode) => (
            <Chip
              key={mode}
              icon={
                mode === 'none'
                  ? 'block'
                  : mode === 'raster'
                    ? 'image'
                    : 'layers'
              }
              variant={state.drape === mode ? 'info' : 'neutral'}
              label={
                mode === 'none'
                  ? 'No drape'
                  : mode === 'raster'
                    ? 'Drape raster'
                    : 'Drape vector'
              }
              pressable
              pressed={state.drape === mode}
              onClick={() => setTerrainToolCard({ drape: mode })}
              data-testid={`terrain-drape-${mode}`}
            />
          ))}
        </div>
      </ToolCardSection>

      <ToolCardSection title="Elevation profile">
        <ElevationProfilePanel
          samples={profileSamples}
          onPickProfile={onPickProfile}
          onOpenFullProfile={onOpenFullProfile}
          readOnly={readOnly}
        />
      </ToolCardSection>

      <ToolCardSection title="Coordinate & elevation">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: tokens.space[2],
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Lng
            </div>
            <div data-testid="terrain-query-lng">
              {state.query ? state.query.lon.toFixed(6) : '—'}
            </div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Lat
            </div>
            <div data-testid="terrain-query-lat">
              {state.query ? state.query.lat.toFixed(6) : '—'}
            </div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Elevation
            </div>
            <div data-testid="terrain-query-elev">
              {state.query?.elevation == null
                ? '—'
                : formatMetres(state.query.elevation)}
            </div>
          </div>
        </div>
      </ToolCardSection>

      <ToolCardSection title="Cut / fill">
        <div style={{ display: 'flex', gap: tokens.space[2], flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onCutFill}
            disabled={readOnly || !onCutFill}
            aria-label="Open cut and fill analysis"
            data-testid="terrain-cut-fill"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor:
                !readOnly && onCutFill ? 'pointer' : 'default',
              opacity: !readOnly && onCutFill ? 1 : 0.5,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
            }}
          >
            <Icon name="difference" size={20} decorative />
            Cut / fill…
          </button>
        </div>
        {!onCutFill ? (
          <Callout severity="info" icon="info">
            Cut / fill analysis is a placeholder; the full workflow lands
            with the geoprocessing work package.
          </Callout>
        ) : null}
      </ToolCardSection>
    </ToolCardShell>
  );
}
